import React, { useState } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  IconButton,
  Slide,
  Paper,
  Typography,
} from '@mui/material';
import {
  Hotel as HotelIcon,
  FlightTakeoff as FlightIcon,
  DirectionsCar as CarIcon,
  LocalActivity as ActivityIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import HotelSearchForm from './HotelSearchForm';
import FlightSearchForm from './FlightSearchForm';
import CarSearchForm from './CarSearchForm';
import ActivitySearchForm from './ActivitySearchForm';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

export type BookingTab = 'hotels' | 'flights' | 'cars' | 'activities';

interface BookingSearchBarProps {
  open: boolean;
  initialTab?: BookingTab;
  onClose: () => void;
}

const tabs: { value: BookingTab; label: string; icon: React.ReactElement }[] = [
  { value: 'hotels', label: 'Hotels', icon: <HotelIcon /> },
  { value: 'flights', label: 'Flights', icon: <FlightIcon /> },
  { value: 'cars', label: 'Cars', icon: <CarIcon /> },
  { value: 'activities', label: 'Activities', icon: <ActivityIcon /> },
];

const BookingSearchBar: React.FC<BookingSearchBarProps> = ({
  open,
  initialTab = 'hotels',
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<BookingTab>(initialTab);
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const bookingEnabled = !flagsLoading && isEnabled('booking_enabled') === true;

  // Update active tab when initialTab changes
  React.useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: BookingTab) => {
    setActiveTab(newValue);
  };

  const renderSearchForm = () => {
    switch (activeTab) {
      case 'hotels':
        return <HotelSearchForm />;
      case 'flights':
        return <FlightSearchForm />;
      case 'cars':
        return <CarSearchForm />;
      case 'activities':
        return <ActivitySearchForm />;
      default:
        return <HotelSearchForm />;
    }
  };

  return (
    <Slide direction="down" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          top: 64, // Below navbar
          left: 0,
          right: 0,
          zIndex: 1200,
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', py: { xs: 1, md: 2 } }}>
            {/* Close button */}
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 0,
                zIndex: 10,
                color: 'text.secondary',
                bgcolor: 'white',
                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <CloseIcon />
            </IconButton>

            {bookingEnabled ? (
              <>
                {/* Tabs */}
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons={false}
                  sx={{
                    mr: { xs: 5, md: 0 },
                    mb: { xs: 1.5, md: 3 },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '1rem',
                      minHeight: 48,
                      color: 'text.secondary',
                      '&.Mui-selected': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                    },
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: '3px 3px 0 0',
                      backgroundColor: 'primary.main',
                    },
                  }}
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      icon={tab.icon}
                      iconPosition="start"
                      label={tab.label}
                      sx={{ gap: 1 }}
                    />
                  ))}
                </Tabs>

                {/* Search Form */}
                <Box sx={{ pb: { xs: 0.5, md: 1 } }}>
                  {renderSearchForm()}
                </Box>
              </>
            ) : (
              /* Coming Soon state — replaces Tabs + Form when booking_enabled is off.
                 Preserves the slide-in/close UX so the user gets clear feedback
                 instead of a no-op tap. */
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  py: { xs: 4, md: 6 },
                  px: 2,
                  mr: { xs: 5, md: 0 },
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 48, color: '#2D6E6A', mb: 1.5 }} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 1, color: '#00796B' }}
                >
                  Booking is coming soon
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 360 }}
                >
                  Live booking for hotels, flights, cars, and activities is on the way. In the meantime, you can plan trips, upload itineraries, and share with friends.
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Paper>
    </Slide>
  );
};

export default BookingSearchBar;
